/**
 * PostHog service — no-op-safe wrapper around posthog-js.
 *
 * All calls are guarded: when VITE_POSTHOG_KEY is absent the module is a
 * no-op so the dev environment works with zero env config.
 *
 * ADR-030 constraints:
 * - person_profiles: 'identified_only' — hard-coded, not configurable
 * - identify() receives userId only — never email, displayName, or plan
 * - maskAllInputs: true
 * - session recording masks email + password inputs and [data-ph-mask] elements
 */

import posthog from 'posthog-js';

const KEY = import.meta.env['VITE_POSTHOG_KEY'] as string | undefined;
const HOST = (import.meta.env['VITE_POSTHOG_HOST'] as string | undefined) ?? 'https://app.posthog.com';

/**
 * Environment is derived from hostname at runtime — staging.<domain> → 'staging',
 * everything else → 'production'. This is the canonical pattern for distinguishing
 * PostHog events across deployment targets within a single PostHog project
 * (Option A from D-05 architectural decision; chosen over two-project approach due
 * to PostHog free-tier per-project limit, ADR pending). The property is attached as
 * a super-property via posthog.register() so it auto-decorates every event including
 * autocapture and $pageview.
 */
const ENVIRONMENT: 'staging' | 'production' =
  typeof window !== 'undefined' && window.location.hostname.startsWith('staging.')
    ? 'staging'
    : 'production';

let initialized = false;

export const posthogService = {
  init(): void {
    if (!KEY) return;
    if (initialized) return;

    posthog.init(KEY, {
      api_host: HOST,
      // Hard-coded per ADR-030 — must never be changed to 'always' or 'never'
      person_profiles: 'identified_only',
      autocapture: true,
      capture_pageview: false, // We fire $pageview manually via use-page-view hook
      capture_pageleave: true,
      session_recording: {
        maskAllInputs: true,
        maskInputOptions: {
          password: true,
          email: true,
        },
        maskTextSelector: '[data-ph-mask]',
      },
      disable_session_recording: false,
    });

    posthog.register({ environment: ENVIRONMENT });

    initialized = true;
  },

  /**
   * Identify the authenticated user.
   * Receives userId only — never email, displayName, or plan (ADR-030).
   */
  identify(userId: string): void {
    if (!KEY || !initialized) return;
    posthog.identify(userId);
  },

  /** Reset identity on sign-out. */
  reset(): void {
    if (!KEY || !initialized) return;
    posthog.reset();
  },

  /** Track a custom event with optional properties. */
  track(event: string, properties?: Record<string, unknown>): void {
    if (!KEY || !initialized) return;
    posthog.capture(event, properties);
  },

  /**
   * Check a PostHog feature flag.
   * Returns false when PostHog is unavailable (offline, key missing).
   */
  isFeatureEnabled(flagName: string): boolean {
    if (!KEY || !initialized) return false;
    return posthog.isFeatureEnabled(flagName) ?? false;
  },
};
